// DUCKS pattern
import { createAction, createSlice } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

import {
  OrganizationDetailsFormInput,
  OrganizationList,
  QuestionBankListDetail,
  QuestionBankDetailResponse,
  WordBankList,
  WordBankSelectedDeatil,
} from 'features/organization/types'
import type { RootState } from 'store/store'

export interface OrgListFilterState {
  isFilterOpen: boolean
  selectedStatusList: number[]
  selectedFeaturesList: number[]
  facilityMinCount: string
  facilityMaxCount: string
  pageSize?: number
}

export interface OrganizationState {
  organizationDetails: OrganizationDetailsFormInput
  organizationList: OrganizationList
  featureList: object[]
  questionBankListDetail: QuestionBankListDetail
  questionList: QuestionBankDetailResponse
  selectedDetail: WordBankSelectedDeatil
  wordBankList: WordBankList
  selectedOrganization: { id: number; name: string }
  activeTab: number
  editQuestionItem?: number
  activeAppConfigTab: number
  selectedOrgId: number | null
  selectedRuleId: number | null
  orgListFilterState: OrgListFilterState
}

const initialState: OrganizationState = {
  organizationDetails: {},
  organizationList: { data: [], totalCount: 0, hasData: true },
  featureList: [],
  questionBankListDetail: {},
  questionList: {},
  selectedDetail: {},
  wordBankList: { data: [], totalCount: 0, hasData: true },
  selectedOrganization: { id: 0, name: '' },
  activeTab: 0,
  editQuestionItem: 0,
  activeAppConfigTab: 0,
  selectedOrgId: null,
  selectedRuleId: null,
  orgListFilterState: {
    isFilterOpen: false,
    selectedStatusList: [],
    selectedFeaturesList: [],
    facilityMinCount: '',
    facilityMaxCount: '',
  },
}

// slice
export const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    setOrganizationList: (state, action) => {
      state.organizationList = action.payload
    },
    setFeaturesList: (state, action) => {
      state.featureList = action.payload
    },
    setQuestionBankListDetail: (state, action) => {
      state.questionBankListDetail = action.payload
    },
    setQuestionList: (state, action) => {
      state.questionList = action.payload
    },
    setWordBankSelectedDetail: (state, action) => {
      state.selectedDetail = action.payload
    },
    setWordBankList: (state, action) => {
      state.wordBankList = action.payload
    },
    setSelectedOrganization: (state, action) => {
      state.selectedOrganization = action.payload
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload
    },
    setEditQuestionItem: (state, action) => {
      state.editQuestionItem = action.payload
    },
    setActiveAppConfigTab: (state, action) => {
      state.activeAppConfigTab = action.payload
    },
    setSelectedOrgId: (state, action) => {
      state.selectedOrgId = action.payload
    },
    setSelectedRuleId: (state, action) => {
      state.selectedRuleId = action.payload
    },
    setOrgListFilterState: (state, action) => {
      state.orgListFilterState = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(PURGE, () => initialState)
  },
})

// Actions
export const organizationActions = {
  organizationCreation: createAction(
    `${organizationSlice.name}/organizationCreation`,
    (organizationData: OrganizationDetailsFormInput) => ({
      payload: organizationData,
    }),
  ),
  setOrganizationList: organizationSlice.actions.setOrganizationList,
  setFeaturesList: organizationSlice.actions.setFeaturesList,
  setQuestionBankListDetail: organizationSlice.actions.setQuestionBankListDetail,
  setQuestionList: organizationSlice.actions.setQuestionList,
  setSelectedDetail: organizationSlice.actions.setWordBankSelectedDetail,
  setWordBankList: organizationSlice.actions.setWordBankList,
  setSelectedOrganizationDetail: organizationSlice.actions.setSelectedOrganization,
  setActiveTab: organizationSlice.actions.setActiveTab,
  setEditQuestionItem: organizationSlice.actions.setEditQuestionItem,
  setActiveAppConfigTab: organizationSlice.actions.setActiveAppConfigTab,
  setSelectedOrgId: organizationSlice.actions.setSelectedOrgId,
  setSelectedRuleId: organizationSlice.actions.setSelectedRuleId,
  setOrgListFilterState: organizationSlice.actions.setOrgListFilterState,
}

// Selectors
export const organizationDetails = (state: RootState) => state.organization.organizationDetails
export const selectOrganizationList = (state: RootState) => state.organization.organizationList
export const selectFeaturesList = (state: RootState) => state.organization.featureList
export const selectQuestionBankDetail = (state: RootState) =>
  state.organization.questionBankListDetail
export const selectQuestionList = (state: RootState) => state.organization.questionList
export const selectedOrganization = (state: RootState) => state.organization.selectedOrganization
export const getActiveTab = (state: RootState) => state.organization.activeTab
export const getEditQuestionItem = (state: RootState) => state.organization.editQuestionItem
export const getActiveAppConfigTab = (state: RootState) => state.organization.activeAppConfigTab
export const getSelectedOrgId = (state: RootState) => state.organization.selectedOrgId
export const getSelectedRuleId = (state: RootState) => state.organization.selectedRuleId
export const getOrgListFilterState = (state: RootState) => state.organization.orgListFilterState

// Reducer
export default organizationSlice.reducer
